import { call, put, select } from 'redux-saga/effects';

import * as ProfileApi from 'api/profile';

import { EventCustomWarningCodes, EventLevels, EventTypes } from 'defs/events';
import { DATA_BAG } from 'defs/offlineAvailability';
import routes from 'defs/routes';
import Sequences from 'defs/sequences';

import EventWrapper from 'models/global/EventWrapper';

import { getIsOnline } from 'store/connection';
import { actionCreators as connectionAC } from 'store/connection';
import { actionCreators as eventsAC } from 'store/events';
import { getIsMigrosAuthenticated } from 'store/login';
import { actionCreators as offlineAvailabilityAC } from 'store/offlineAvailability';
import { actionCreators as profileAC, getHasUserBeenLoaded, getIsLoadingProfile } from 'store/profile';
import { getActiveProgramId } from 'store/programs';
import { actionCreators as sequencesAC } from 'store/sequences';
import { actionCreators as viewAC } from 'store/view';

export function* getUserData(action?: any) {
	const isOnline = yield select(getIsOnline);
	if (!isOnline) {
		return;
	}

	action = action || {
		force: false,
		preventUpdateLoadingState: false,
	};

	const preventUpdateLoadingState: boolean =
		typeof action.preventUpdateLoadingState === 'boolean' ? action.preventUpdateLoadingState : false;

	try {
		const isMigrosAuthenticated = yield select(getIsMigrosAuthenticated);
		const isLoading = yield select(getIsLoadingProfile);
		const alreadyLoaded = yield select(getHasUserBeenLoaded);

		if (isLoading) {
			return void 0;
		}

		if (!isMigrosAuthenticated) {
			return void 0;
		}

		if (!alreadyLoaded || action.force) {
			if (!preventUpdateLoadingState) {
				yield put(viewAC.doRequestViewLoading(routes.profile.indexRoute.id, true));
				yield put(profileAC.doLoadUserDataLoading(true));
			}

			const userData = yield call(ProfileApi.getUserData);
			const model = new EventWrapper(userData);

			yield put(profileAC.doLoadUserDataSuccess(model.getData()));
			yield put(eventsAC.doAddEvents(model.getEvents()));

			if (!preventUpdateLoadingState) {
				yield put(profileAC.doLoadUserDataLoading(false));
				yield put(viewAC.doRequestViewSuccess(routes.profile.indexRoute.id));
			}

			yield put(offlineAvailabilityAC.doRegisterDataBag(DATA_BAG.PROFILE));
		} else {
			yield put(profileAC.doLoadUserDataNoChange());
		}
	} catch (err) {
		yield put(profileAC.doLoadUserDataError(err));
		yield put(viewAC.doRequestViewFailure(routes.profile.indexRoute.id));

		if (!(err.toString().search('status code 401') >= 0)) {
			yield put(
				eventsAC.doAddEvents([
					{
						eventLevel: EventLevels.NotAvailable as any,
						code: EventCustomWarningCodes.ProfileLoading,
						eventType: EventTypes.Warning,
						event: undefined as any,
					},
				])
			);
		}
	}

	yield put(sequencesAC.doFinishSequence(Sequences.ANAMNESIS_IS_PROCESSING_STEP));
	return;
}

export function* saveCoach(action: any) {
	const isOnline = yield select(getIsOnline);
	if (!isOnline) {
		return yield put(connectionAC.doTrackNetworkSaveRequestError());
	}

	yield put(sequencesAC.doStartSequence(Sequences.ANAMNESIS_IS_PROCESSING_STEP));

	try {
		const answer = yield call(ProfileApi.selectCoach, action.data);
		const model = new EventWrapper(answer);

		yield put(profileAC.doSaveCoachSuccess(model.getData()));
		yield put(eventsAC.doAddEvents(model.getEvents()));
	} catch (err) {
		yield put(profileAC.doSaveCoachError(err));
	}
}

export function* saveParticipateCompetition(action: any) {
	const isOnline = yield select(getIsOnline);
	if (!isOnline) {
		return yield put(connectionAC.doTrackNetworkSaveRequestError());
	}

	const programId = yield select(getActiveProgramId);
	try {
		yield call(ProfileApi.setParticipateCompetition, {
			programId,
			participate: action.isParticipating,
		});
	} catch (err) {
		// do nothing
	}
}

export function* saveGender(action: any) {
	const isOnline = yield select(getIsOnline);
	if (!isOnline) {
		return yield put(connectionAC.doTrackNetworkSaveRequestError());
	}

	yield put(sequencesAC.doStartSequence(Sequences.ANAMNESIS_IS_PROCESSING_STEP));

	try {
		const answer = yield call(ProfileApi.selectGender, action.data);
		const model = new EventWrapper(answer);

		yield put(profileAC.doSaveGenderSuccess(model.getData()));
		yield put(eventsAC.doAddEvents(model.getEvents()));
	} catch (err) {
		yield put(profileAC.doSaveGenderError(err));
	}

	yield put(sequencesAC.doFinishSequence(Sequences.ANAMNESIS_IS_PROCESSING_STEP));
}

export function* saveDsgvo(action: any) {
	const isOnline = yield select(getIsOnline);
	if (!isOnline) {
		return yield put(connectionAC.doTrackNetworkSaveRequestError());
	}

	yield put(sequencesAC.doStartSequence(Sequences.ANAMNESIS_IS_PROCESSING_STEP));

	try {
		const answer = yield call(ProfileApi.setDsgvo, action.data);
		const model = new EventWrapper(answer);

		yield put(profileAC.doSaveDsgvoSuccess(model.getData()));
		yield put(eventsAC.doAddEvents(model.getEvents()));
	} catch (err) {
		yield put(profileAC.doSaveDsgvoError(err));
	}

	yield put(sequencesAC.doFinishSequence(Sequences.ANAMNESIS_IS_PROCESSING_STEP));
}

export function* saveAge(action: any) {
	const isOnline = yield select(getIsOnline);
	if (!isOnline) {
		return yield put(connectionAC.doTrackNetworkSaveRequestError());
	}

	yield put(sequencesAC.doStartSequence(Sequences.ANAMNESIS_IS_PROCESSING_STEP));

	try {
		const answer = yield call(ProfileApi.setBirthdate, action.data);
		const model = new EventWrapper(answer);

		yield put(profileAC.doSaveAgeSuccess(model.getData()));
		yield put(eventsAC.doAddEvents(model.getEvents()));
	} catch (err) {
		yield put(profileAC.doSaveAgeError(err));
	}

	yield put(sequencesAC.doFinishSequence(Sequences.ANAMNESIS_IS_PROCESSING_STEP));
}

export function* saveHeight(action: any) {
	const isOnline = yield select(getIsOnline);
	if (!isOnline) {
		return yield put(connectionAC.doTrackNetworkSaveRequestError());
	}

	yield put(sequencesAC.doStartSequence(Sequences.ANAMNESIS_IS_PROCESSING_STEP));

	try {
		const answer = yield call(ProfileApi.setHeight, action.data);
		const model = new EventWrapper(answer);

		yield put(profileAC.doSaveHeightSuccess(model.getData()));
		yield put(eventsAC.doAddEvents(model.getEvents()));
	} catch (err) {
		yield put(profileAC.doSaveHeightError(err));
	}

	yield put(sequencesAC.doFinishSequence(Sequences.ANAMNESIS_IS_PROCESSING_STEP));
}

export function* saveWeight(action: any) {
	const isOnline = yield select(getIsOnline);
	if (!isOnline) {
		return yield put(connectionAC.doTrackNetworkSaveRequestError());
	}

	yield put(sequencesAC.doStartSequence(Sequences.ANAMNESIS_IS_PROCESSING_STEP));

	try {
		const answer = yield call(ProfileApi.setWeight, action.data);
		const model = new EventWrapper(answer);

		yield put(profileAC.doSaveWeightSuccess(model.getData()));
		yield put(eventsAC.doAddEvents(model.getEvents()));
	} catch (err) {
		yield put(profileAC.doSaveWeightError(err));
	}

	yield put(sequencesAC.doFinishSequence(Sequences.ANAMNESIS_IS_PROCESSING_STEP));
}

export function* saveHealthTarget(action: {
	type: 'PROFILE/SAVE_HEALTH_TARGET';
	data: ProfileApi.RequestSetTempHealthTarget;
}) {
	const isOnline = yield select(getIsOnline);
	if (!isOnline) {
		return yield put(connectionAC.doTrackNetworkSaveRequestError());
	}

	yield put(sequencesAC.doStartSequence(Sequences.ANAMNESIS_IS_PROCESSING_STEP));

	try {
		const answer: ProfileApi.ResponseSetTempHealthTarget = yield call(ProfileApi.setTempHealthTarget, action.data);
		const model = new EventWrapper<ProfileApi.ResponseSetTempHealthTargetData>(answer);

		yield put(profileAC.doSaveHealthTargetSuccess(model.getData()));
		yield put(eventsAC.doAddEvents(model.getEvents()));
	} catch (err) {
		yield put(profileAC.doSaveHealthTargetError(err));
	}

	yield put(sequencesAC.doFinishSequence(Sequences.ANAMNESIS_IS_PROCESSING_STEP));
}

export function* saveAcceptHealthTarget(action: any) {
	const isOnline = yield select(getIsOnline);
	if (!isOnline) {
		return yield put(connectionAC.doTrackNetworkSaveRequestError());
	}

	yield put(sequencesAC.doStartSequence(Sequences.ANAMNESIS_IS_PROCESSING_STEP));

	try {
		const answer = yield call(ProfileApi.setHealthTarget, action.data);
		const model = new EventWrapper(answer);

		yield put(profileAC.doSaveAcceptHealthTargetSuccess(model.getData()));
		yield put(eventsAC.doAddEvents(model.getEvents()));
	} catch (err) {
		yield put(profileAC.doSaveAcceptHealthTargetError(err));
	}

	yield put(sequencesAC.doFinishSequence(Sequences.ANAMNESIS_IS_PROCESSING_STEP));
}

export function* saveActivityLevel(action: any) {
	const isOnline = yield select(getIsOnline);
	if (!isOnline) {
		return yield put(connectionAC.doTrackNetworkSaveRequestError());
	}

	yield put(sequencesAC.doStartSequence(Sequences.ANAMNESIS_IS_PROCESSING_STEP));

	try {
		const answer = yield call(ProfileApi.setActivityLevel, action.data);
		const model = new EventWrapper(answer);

		yield put(profileAC.doSaveActivityLevelSuccess(model.getData()));
		yield put(eventsAC.doAddEvents(model.getEvents()));
	} catch (err) {
		yield put(profileAC.doSaveActivityLevelError(err));
	}

	yield put(sequencesAC.doFinishSequence(Sequences.ANAMNESIS_IS_PROCESSING_STEP));
}

export function* saveDietaryHabits(action: any) {
	const isOnline = yield select(getIsOnline);
	if (!isOnline) {
		return yield put(connectionAC.doTrackNetworkSaveRequestError());
	}

	yield put(sequencesAC.doStartSequence(Sequences.ANAMNESIS_IS_PROCESSING_STEP));

	try {
		const answer = yield call(ProfileApi.setDietaryHabits, action.data);
		const model = new EventWrapper(answer);

		yield put(profileAC.doSaveDietaryHabitsSuccess(model.getData()));
		yield put(eventsAC.doAddEvents(model.getEvents()));
	} catch (err) {
		yield put(profileAC.doSaveDietaryHabitsError(err));
	}

	yield put(sequencesAC.doFinishSequence(Sequences.ANAMNESIS_IS_PROCESSING_STEP));
}

export function* finish(action: any) {
	const isOnline = yield select(getIsOnline);
	if (!isOnline) {
		return yield put(connectionAC.doTrackNetworkSaveRequestError());
	}

	yield put(sequencesAC.doStartSequence(Sequences.ANAMNESIS_IS_PROCESSING_STEP));

	// FIXME: ProfileApi.finish does not exist anymore!
	try {
		const answer = yield call(ProfileApi.finish);
		const model = new EventWrapper(answer);

		yield put(profileAC.doFinishSuccess(model.getData()));
		yield put(eventsAC.doAddEvents(model.getEvents()));
		yield* getUserData() as any;
	} catch (err) {
		yield put(profileAC.doFinishError(err));
	}

	yield put(sequencesAC.doFinishSequence(Sequences.ANAMNESIS_IS_PROCESSING_STEP));
}

export function* finishOnboarding() {
	const isOnline = yield select(getIsOnline);
	if (!isOnline) {
		return yield put(connectionAC.doTrackNetworkSaveRequestError());
	}

	try {
		yield put(profileAC.doFinishOnboardingLoading(true));
		const answer = yield call(ProfileApi.finishOnboarding);
		const model = new EventWrapper(answer);

		yield put(profileAC.doFinishOnboardingSuccess(model.getData()));
		yield put(eventsAC.doAddEvents(model.getEvents()));
		yield put(profileAC.doFinishOnboardingLoading(false));
	} catch (err) {
		yield put(profileAC.doFinishOnboardingError(err));
	}
}

export function* putSlogan(action: any) {
	const isOnline = yield select(getIsOnline);
	if (!isOnline) {
		return yield put(connectionAC.doTrackNetworkSaveRequestError());
	}

	try {
		const savedItem = yield call(ProfileApi.putSlogan, action.slogan);
		const model = new EventWrapper(savedItem);

		yield put(profileAC.doSaveSloganSuccess(model.getData().slogan));
		yield put(eventsAC.doAddEvents(model.getEvents()));
	} catch (err) {
		yield put(profileAC.doSaveSloganError());
	}
}
